import { useState, useEffect, useCallback } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig/firebase';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';

// Función para subir la foto de perfil a Firebase Storage
async function subirFotoPerfil(uid, photoURL) {
  try {
    const response = await fetch(photoURL);
    const blob = await response.blob();
    const storage = getStorage();
    const storageRef = ref(storage, `avatars/${uid}.jpg`);
    await uploadBytes(storageRef, blob);
    return await getDownloadURL(storageRef);
  } catch (error) {
    console.error('Error subiendo foto de perfil a Storage:', error);
    return null;
  }
}

export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refreshAuth = useCallback(async () => {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (currentUser) {
      try {
        const userRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userRef);
        let userData = userDoc.exists() ? userDoc.data() : {};

        // Si no tiene avatarURL pero sí photoURL, sube la imagen a Storage
        if (!userData.avatarURL && currentUser.photoURL) {
          const url = await subirFotoPerfil(currentUser.uid, currentUser.photoURL);
          if (url) {
            await updateDoc(userRef, { avatarURL: url });
            userData.avatarURL = url;
          }
        }

        setUser({
          ...currentUser,
          ...userData
        });
      } catch (err) {
        console.error('Error al refrescar datos del usuario:', err);
        setError(err);
      }
    } else {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      try {
        if (authUser) {
          const userRef = doc(db, 'users', authUser.uid);
          let userDoc = await getDoc(userRef);

          if (!userDoc.exists()) {
            // Crear el documento con nivel 0 y datos básicos
            await setDoc(userRef, {
              uid: authUser.uid,
              email: authUser.email,
              displayName: authUser.displayName || '',
              nivelSuscripcion: 0,
              roles: ['free'],
              isActive: true,
              createdAt: serverTimestamp(),
              lastLogin: serverTimestamp(),
              photoURL: authUser.photoURL || '',
              extraData: {}
            });
            userDoc = await getDoc(userRef);
          }

          let userData = userDoc.data();

          // Si no tiene avatarURL pero sí photoURL, sube la imagen a Storage
          if (!userData.avatarURL && authUser.photoURL) {
            const url = await subirFotoPerfil(authUser.uid, authUser.photoURL);
            if (url) {
              await updateDoc(userRef, { avatarURL: url });
              userData.avatarURL = url;
            }
          }

          setUser({
            ...authUser,
            ...userData
          });
        } else {
          setUser(null);
        }
      } catch (err) {
        console.error('Error al obtener datos del usuario:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const isAdmin = () => {
    return user?.roles?.includes('admin') || false;
  };

  const isPremium = () => {
    return user?.roles?.includes('premium') || false;
  };

  const hasSubscriptionLevel = (level) => {
    return user?.subscriptionLevel >= level || false;
  };

  const canAccess = (requiredRole, requiredLevel = 0) => {
    if (!user) return false;
    
    // Si se requiere un rol específico
    if (requiredRole && !user.roles?.includes(requiredRole)) {
      return false;
    }

    // Si se requiere un nivel de suscripción
    if (requiredLevel > 0) {
      return hasSubscriptionLevel(requiredLevel);
    }

    return true;
  };

  return {
    user,
    loading,
    error,
    isAdmin,
    isPremium,
    hasSubscriptionLevel,
    canAccess,
    refreshAuth
  };
}; 